import { OrderingDirection, OrderingType, Status } from '../logic/graphql/types/types.d';
import { stringifyStatus } from './type-formatters';
import { IntlShape } from 'react-intl';

type StatusOptionsFilters = 'nopartialorexpired' | 'nopartial';

type OptionType = {
  value: any;
  label: string;
};

export const getStatusOptions = (
  intl: IntlShape,
  filter?: StatusOptionsFilters,
): Array<OptionType> => {
  const options: Array<OptionType> = [
    { value: 'all', label: intl.formatMessage({ id: 'order.status.all' }) },
    { value: Status.Active, label: stringifyStatus(Status.Active, intl) },
    { value: Status.Partial, label: stringifyStatus(Status.Partial, intl) },
    { value: Status.Completed, label: stringifyStatus(Status.Completed, intl) },
    { value: Status.Canceled, label: stringifyStatus(Status.Canceled, intl) },
    { value: Status.Expired, label: stringifyStatus(Status.Expired, intl) },
    { value: Status.Pending, label: stringifyStatus(Status.Pending, intl) },
    { value: Status.Rejected, label: stringifyStatus(Status.Rejected, intl) },
  ];

  if (filter === 'nopartial') {
    return options.filter((o) => o.value !== Status.Partial);
  }
  if (filter === 'nopartialorexpired') {
    return options.filter((o) => o.value !== Status.Partial && o.value !== Status.Expired);
  }
  return options;
};

export type OrderingOptionsType = {
  value: { direction: OrderingDirection; type: OrderingType };
  label: string;
};

export const getOrderingOptions = (intl: IntlShape): Array<OrderingOptionsType> => {
  return [
    {
      value: { direction: OrderingDirection.Desc, type: OrderingType.TransferDate },
      label: intl.formatMessage({ id: 'sortOrderOptions.transfer_date_desc' }),
    },
    {
      value: { direction: OrderingDirection.Asc, type: OrderingType.TransferDate },
      label: intl.formatMessage({ id: 'sortOrderOptions.transfer_date_asc' }),
    },
    {
      value: { direction: OrderingDirection.Desc, type: OrderingType.TransitionDate },
      label: intl.formatMessage({ id: 'sortOrderOptions.transition_date_desc' }),
    },
    {
      value: { direction: OrderingDirection.Asc, type: OrderingType.TransitionDate },
      label: intl.formatMessage({ id: 'sortOrderOptions.transition_date_asc' }),
    },
    {
      value: { direction: OrderingDirection.Desc, type: OrderingType.ValidityStart },
      label: intl.formatMessage({ id: 'sortOrderOptions.validity_start_desc' }),
    },
    {
      value: { direction: OrderingDirection.Asc, type: OrderingType.ValidityStart },
      label: intl.formatMessage({ id: 'sortOrderOptions.validity_start_asc' }),
    },
    {
      value: { direction: OrderingDirection.Desc, type: OrderingType.ValidityEnd },
      label: intl.formatMessage({ id: 'sortOrderOptions.validity_end_desc' }),
    },
    {
      value: { direction: OrderingDirection.Asc, type: OrderingType.ValidityEnd },
      label: intl.formatMessage({ id: 'sortOrderOptions.validity_end_asc' }),
    },
    {
      value: { direction: OrderingDirection.Desc, type: OrderingType.OrderDate },
      label: intl.formatMessage({ id: 'sortOrderOptions.order_date_desc' }),
    },
    {
      value: { direction: OrderingDirection.Asc, type: OrderingType.OrderDate },
      label: intl.formatMessage({ id: 'sortOrderOptions.order_date_asc' }),
    },
    {
      value: { direction: OrderingDirection.Desc, type: OrderingType.FromValue },
      label: intl.formatMessage({ id: 'sortOrderOptions.from_value_desc' }),
    },
    {
      value: { direction: OrderingDirection.Asc, type: OrderingType.FromValue },
      label: intl.formatMessage({ id: 'sortOrderOptions.from_value_asc' }),
    },
    {
      value: { direction: OrderingDirection.Desc, type: OrderingType.Security },
      label: intl.formatMessage({ id: 'sortOrderOptions.security_desc' }),
    },
    {
      value: { direction: OrderingDirection.Asc, type: OrderingType.Security },
      label: intl.formatMessage({ id: 'sortOrderOptions.security_asc' }),
    },
    {
      value: { direction: OrderingDirection.Desc, type: OrderingType.Status },
      label: intl.formatMessage({ id: 'sortOrderOptions.status_desc' }),
    },
    {
      value: { direction: OrderingDirection.Asc, type: OrderingType.Status },
      label: intl.formatMessage({ id: 'sortOrderOptions.status_asc' }),
    },
    {
      value: { direction: OrderingDirection.Desc, type: OrderingType.TransactionCode },
      label: intl.formatMessage({ id: 'sortOrderOptions.transaction_code_desc' }),
    },
    {
      value: { direction: OrderingDirection.Asc, type: OrderingType.TransactionCode },
      label: intl.formatMessage({ id: 'sortOrderOptions.transaction_code_asc' }),
    },
    {
      value: { direction: OrderingDirection.Desc, type: OrderingType.Currency },
      label: intl.formatMessage({ id: 'sortOrderOptions.currency_desc' }),
    },
    {
      value: { direction: OrderingDirection.Asc, type: OrderingType.Currency },
      label: intl.formatMessage({ id: 'sortOrderOptions.currency_asc' }),
    },
    {
      value: { direction: OrderingDirection.Desc, type: OrderingType.Direction },
      label: intl.formatMessage({ id: 'sortOrderOptions.direction_desc' }),
    },
    {
      value: { direction: OrderingDirection.Asc, type: OrderingType.Direction },
      label: intl.formatMessage({ id: 'sortOrderOptions.direction_asc' }),
    },
    {
      value: { direction: OrderingDirection.Desc, type: OrderingType.Count },
      label: intl.formatMessage({ id: 'sortOrderOptions.count_desc' }),
    },
    {
      value: { direction: OrderingDirection.Asc, type: OrderingType.Count },
      label: intl.formatMessage({ id: 'sortOrderOptions.count_asc' }),
    },
    {
      value: { direction: OrderingDirection.Desc, type: OrderingType.LimitPrice },
      label: intl.formatMessage({ id: 'sortOrderOptions.limit_price_desc' }),
    },
    {
      value: { direction: OrderingDirection.Asc, type: OrderingType.LimitPrice },
      label: intl.formatMessage({ id: 'sortOrderOptions.limit_price_asc' }),
    },
    {
      value: { direction: OrderingDirection.Desc, type: OrderingType.ActivationPrice },
      label: intl.formatMessage({ id: 'sortOrderOptions.activation_price_desc' }),
    },
    {
      value: { direction: OrderingDirection.Asc, type: OrderingType.ActivationPrice },
      label: intl.formatMessage({ id: 'sortOrderOptions.activation_price_asc' }),
    },
    {
      value: { direction: OrderingDirection.Desc, type: OrderingType.Value },
      label: intl.formatMessage({ id: 'sortOrderOptions.value_desc' }),
    },
    {
      value: { direction: OrderingDirection.Asc, type: OrderingType.Value },
      label: intl.formatMessage({ id: 'sortOrderOptions.value_asc' }),
    },
    {
      value: { direction: OrderingDirection.Desc, type: OrderingType.FromAccount },
      label: intl.formatMessage({ id: 'sortOrderOptions.from_account_desc' }),
    },
    {
      value: { direction: OrderingDirection.Asc, type: OrderingType.FromAccount },
      label: intl.formatMessage({ id: 'sortOrderOptions.from_account_asc' }),
    },
    {
      value: { direction: OrderingDirection.Desc, type: OrderingType.ToAccount },
      label: intl.formatMessage({ id: 'sortOrderOptions.to_account_desc' }),
    },
    {
      value: { direction: OrderingDirection.Asc, type: OrderingType.ToAccount },
      label: intl.formatMessage({ id: 'sortOrderOptions.to_account_asc' }),
    },
  ];
};

const ExchangeOrderOrderingTypes = [
  OrderingType.Status,
  OrderingType.TransactionCode,
  OrderingType.Security,
  OrderingType.Direction,
  OrderingType.Count,
  OrderingType.LimitPrice,
  OrderingType.ActivationPrice,
  OrderingType.Value,
  OrderingType.ValidityStart,
  OrderingType.ValidityEnd,
];

export const getExchangeOrderOrderingOptions = (intl: IntlShape): Array<OrderingOptionsType> => {
  return getOrderingOptions(intl).filter((o) =>
    ExchangeOrderOrderingTypes.some((t) => t === o.value.type),
  );
};

const UnitOrderingTypes = [
  OrderingType.Status,
  OrderingType.TransactionCode,
  OrderingType.Security,
  OrderingType.Direction,
  OrderingType.Count,
  OrderingType.ValidityStart,
  OrderingType.ValidityEnd,
];

export const getUnitOrderingOptions = (intl: IntlShape): Array<OrderingOptionsType> => {
  return getOrderingOptions(intl).filter((o) => UnitOrderingTypes.some((t) => t === o.value.type));
};

const MoneyTransitionOrderingTypes = [
  OrderingType.Status,
  OrderingType.TransactionCode,
  OrderingType.FromAccount,
  OrderingType.ToAccount,
  OrderingType.Value,
  OrderingType.Currency,
  OrderingType.OrderDate,
];

export const getMoneyTransitionOrderingOptions = (intl: IntlShape): Array<OrderingOptionsType> => {
  return getOrderingOptions(intl).filter((o) =>
    MoneyTransitionOrderingTypes.some((t) => t === o.value.type),
  );
};

const SecurityTransitionsOrderingTypes = [
  OrderingType.Status,
  OrderingType.TransactionCode,
  OrderingType.FromAccount,
  OrderingType.ToAccount,
  OrderingType.Security,
  OrderingType.Count,
  OrderingType.OrderDate,
];

export const getSecurityTransitionsOrderingOptions = (
  intl: IntlShape,
): Array<OrderingOptionsType> => {
  return getOrderingOptions(intl).filter((o) =>
    SecurityTransitionsOrderingTypes.some((t) => t === o.value.type),
  );
};

const MoneyTransfersOderingTypes = [
  OrderingType.Status,
  OrderingType.TransactionCode,
  OrderingType.FromAccount,
  OrderingType.Value,
  OrderingType.Currency,
  OrderingType.TransferDate,
];

export const getMoneyTransfersOrderingOptions = (intl: IntlShape): Array<OrderingOptionsType> => {
  return getOrderingOptions(intl).filter((o) =>
    MoneyTransfersOderingTypes.some((t) => t === o.value.type),
  );
};

const SecurityTransfersOrderingTypes = [
  OrderingType.Status,
  OrderingType.TransactionCode,
  OrderingType.FromAccount,
  OrderingType.Security,
  OrderingType.Count,
  OrderingType.TransferDate,
];

export const getSecurityTransfersOrderingOptions = (
  intl: IntlShape,
): Array<OrderingOptionsType> => {
  return getOrderingOptions(intl).filter((o) =>
    SecurityTransfersOrderingTypes.some((t) => t === o.value.type),
  );
};

const CurrencyConversionsOderingTypes = [
  OrderingType.Status,
  OrderingType.TransactionCode,
  OrderingType.FromValue,
  OrderingType.Currency,
  OrderingType.OrderDate,
];

export const getCurrencyConversionsOrderingOptions = (
  intl: IntlShape,
): Array<OrderingOptionsType> => {
  return getOrderingOptions(intl).filter((o) =>
    CurrencyConversionsOderingTypes.some((t) => t === o.value.type),
  );
};